import {
  FilterIcon,
  SortAscendingIcon,
  SortDescendingIcon,
  ExternalLinkIcon,
  ExclamationIcon,
} from "@heroicons/react/outline";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";

import { useReportContext, SortConfigItem } from "../../report-context";

import { BasicTooltip } from "@/components/ui/Tooltip";
import { Button } from "@/components/ui/button";
import { ColumnType, ReportColumn } from "@/types/evaluate";
import { useSearchOptional } from "@/components/ui/table/Search/search-store";
import { usePromptRegistryObjects } from "@/queries";
import { useAuth } from "@/context/auth-context";

interface HeaderIndicatorsProps {
  column: ReportColumn;
  isDraft?: boolean;
}

export const HeaderIndicators = observer(({ column, isDraft }: HeaderIndicatorsProps) => {
  const { sortConfig } = useReportContext();
  const searchStore = useSearchOptional();
  const { workspaceId } = useParams();
  const auth = useAuth();

  const columnId = column.id?.toString() || "";

  const hasActiveFilters = (searchStore?.filters[columnId]?.length || 0) > 0;

  const activeSortConfig = sortConfig.find(
    (config: SortConfigItem) => config.columnId === column.id,
  );
  const hasActiveSorting = activeSortConfig && activeSortConfig.direction !== null;

  const versionNumber = column?.configuration?.template?.version_number;
  const promptName = column?.configuration?.template?.name;
  let promptId = column?.configuration?.frozen_template_data?.prompt_id;

  const { data: promptRegistryObjects } = usePromptRegistryObjects(auth!.userToken!, {
    workspaceId: Number(workspaceId),
  });

  if (!promptId) {
    const foundPrompt = promptRegistryObjects?.pages
      .flatMap(page => page.items)
      .find(p => p.prompt_name === promptName);
    promptId = foundPrompt?.id;
  }

  const baseURL = `/workspace/${workspaceId}/prompt/${promptId}`;
  const url = versionNumber ? `${baseURL}/version/${versionNumber}` : baseURL;
  const errorMessage = column.errors?.map(error => error.message).join("; ");
  const hasExternalLinkIcon = column.column_type === ColumnType.PROMPT_TEMPLATE;

  return (
    <div className="flex flex-shrink-0 items-center gap-1">
      {hasActiveFilters && (
        <BasicTooltip content="Has active filters">
          <div className="flex h-5 w-5 items-center justify-center rounded bg-blue-100">
            <FilterIcon className="h-4 w-4 text-blue-600" />
          </div>
        </BasicTooltip>
      )}

      {hasActiveSorting && (
        <BasicTooltip content="Has active sorting">
          <div className="flex h-5 w-5 items-center justify-center rounded bg-purple-100">
            {activeSortConfig?.direction === "asc" ? (
              <SortAscendingIcon className="h-4 w-4 text-purple-600" />
            ) : (
              <SortDescendingIcon className="h-4 w-4 text-purple-600" />
            )}
          </div>
        </BasicTooltip>
      )}

      {hasExternalLinkIcon && (
        <BasicTooltip content={`Open ${promptName} in new tab`}>
          <Button
            size="tinyIcon"
            variant="ghost"
            className="h-5 w-5 p-0"
            onClick={e => {
              e.stopPropagation();
              window.open(url, "_blank");
            }}
          >
            <ExternalLinkIcon className="h-4 w-4 text-gray-600" />
          </Button>
        </BasicTooltip>
      )}

      {errorMessage && (
        <BasicTooltip content={errorMessage}>
          <div className="flex h-5 w-5 items-center justify-center rounded bg-red-100">
            <ExclamationIcon className="h-4 w-4 text-red-600" />
          </div>
        </BasicTooltip>
      )}

      {isDraft && (
        <div className="flex items-center gap-1">
          <div className="h-2 w-2 rounded-full bg-amber-500" />
          <span className="text-xs font-medium text-amber-600">Draft</span>
        </div>
      )}
    </div>
  );
});

HeaderIndicators.displayName = "HeaderIndicators";
